/**
 * Admin Login Page - Separate login for administrators
 * Includes password recovery with controlled access
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Shield, Lock, KeyRound, Eye, EyeOff, CheckCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: ''
  });

  // Recovery Modal State
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryStep, setRecoveryStep] = useState(1); // 1: access key, 2: new password
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryForm, setRecoveryForm] = useState({
    accessKey: '',
    email: 'admin@g2gente.com',
    newPassword: '',
    confirmPassword: ''
  });
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      
      // Only allow admin users
      if (user.role !== 'ADMIN') {
        toast.error('Acesso restrito a administradores');
        return;
      }
      
      toast.success('Login administrativo realizado!');
      navigate('/admin');
    } catch (error) {
      let errorMessage = 'Credenciais inválidas';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRecoveryAccessKey = () => {
    // Verify the access key
    if (recoveryForm.accessKey === 'G2rh2026.1') {
      setRecoveryStep(2);
      toast.success('Acesso autorizado!');
    } else {
      toast.error('Chave de acesso inválida');
    }
  };

  const handleRecoverySubmit = async () => {
    if (recoveryForm.newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (recoveryForm.newPassword !== recoveryForm.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    setRecoveryLoading(true);
    try {
      const response = await axios.post(`${API}/api/seed/reset-admin-password`, {
        secret_key: 'g2gente_admin_2024_secret',
        email: recoveryForm.email,
        new_password: recoveryForm.newPassword
      });
      
      toast.success('Senha alterada com sucesso!');
      setShowRecoveryModal(false);
      setRecoveryStep(1);
      setRecoveryForm({
        accessKey: '',
        email: 'admin@g2gente.com',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao resetar senha';
      
      // If user not found, try to create admin
      if (message.includes('not found') || message.includes('não encontrado')) {
        try {
          await axios.post(`${API}/api/seed/admin`, {
            secret: 'G2GENTE_SEED_2026_SECURE',
            email: recoveryForm.email,
            password: recoveryForm.newPassword
          });
          toast.success('Conta admin criada com sucesso!');
          setShowRecoveryModal(false);
          setRecoveryStep(1);
          setRecoveryForm({
            accessKey: '',
            email: 'admin@g2gente.com',
            newPassword: '',
            confirmPassword: ''
          });
        } catch (createError) {
          toast.error('Erro ao criar conta admin');
        }
      } else {
        toast.error(message);
      }
    } finally {
      setRecoveryLoading(false);
    }
  };

  const openRecoveryModal = () => {
    setShowRecoveryModal(true);
    setRecoveryStep(1);
    setRecoveryForm({
      accessKey: '',
      email: 'admin@g2gente.com',
      newPassword: '',
      confirmPassword: ''
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <div className="w-full max-w-md">
        <Button 
          variant="ghost" 
          className="mb-6 text-slate-400 hover:text-white"
          onClick={() => navigate('/login')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar ao login normal
        </Button>

        <Card className="border-slate-700 bg-slate-800/50 backdrop-blur-sm shadow-2xl">
          <CardHeader className="space-y-1 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
                <Shield className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-white">
              Área Administrativa
            </CardTitle>
            <CardDescription className="text-slate-400">
              Acesso restrito a administradores do sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@empresa.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="admin-login-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500 pr-10"
                    data-testid="admin-login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold"
                disabled={loading}
                data-testid="admin-login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Acessar Painel
                  </>
                )}
              </Button>
            </form>
            
            {/* Password Recovery Link */}
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={openRecoveryModal}
                className="text-sm text-amber-400 hover:text-amber-300 hover:underline flex items-center justify-center gap-1 mx-auto"
              >
                <KeyRound className="w-3 h-3" />
                Recuperar acesso administrativo
              </button>
            </div>
            
            <div className="mt-6 p-3 bg-slate-700/30 rounded-lg border border-slate-600/50">
              <p className="text-xs text-slate-400 text-center">
                Esta área é protegida e monitorada. Tentativas de acesso não autorizado serão registradas.
              </p>
            </div>
          </CardContent>
        </Card>
        
        <p className="text-center text-slate-500 text-sm mt-6">
          G2eGente - Sistema de Recrutamento Ético
        </p>
      </div>

      {/* Password Recovery Modal */}
      <Dialog open={showRecoveryModal} onOpenChange={setShowRecoveryModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <KeyRound className="w-5 h-5" />
              Recuperação de Acesso Admin
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {recoveryStep === 1 
                ? 'Digite a chave de acesso para continuar'
                : 'Defina a nova senha do administrador'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {recoveryStep === 1 ? (
              <>
                {/* Step 1: Access Key */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Chave de Acesso</Label>
                  <Input
                    type="password"
                    placeholder="Digite a chave de acesso"
                    value={recoveryForm.accessKey}
                    onChange={(e) => setRecoveryForm({ ...recoveryForm, accessKey: e.target.value })}
                    className="bg-slate-700/50 border-slate-600 text-white"
                    data-testid="recovery-access-key"
                  />
                  <p className="text-xs text-slate-500">
                    A chave de acesso é fornecida pelo suporte técnico
                  </p>
                </div>

                <Button 
                  onClick={handleRecoveryAccessKey}
                  className="w-full bg-amber-600 hover:bg-amber-500"
                  disabled={!recoveryForm.accessKey}
                >
                  Verificar Chave
                </Button>
              </>
            ) : (
              <>
                {/* Step 2: New Password */}
                <div className="p-3 bg-emerald-900/30 border border-emerald-700/50 rounded-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <span className="text-sm text-emerald-300">Acesso autorizado</span>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Email do Admin</Label>
                  <Input
                    type="email"
                    value={recoveryForm.email}
                    onChange={(e) => setRecoveryForm({ ...recoveryForm, email: e.target.value })}
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Nova Senha</Label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Mínimo 6 caracteres"
                      value={recoveryForm.newPassword}
                      onChange={(e) => setRecoveryForm({ ...recoveryForm, newPassword: e.target.value })}
                      className="bg-slate-700/50 border-slate-600 text-white pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-300">Confirmar Nova Senha</Label>
                  <Input
                    type="password"
                    placeholder="Repita a senha"
                    value={recoveryForm.confirmPassword}
                    onChange={(e) => setRecoveryForm({ ...recoveryForm, confirmPassword: e.target.value })}
                    className="bg-slate-700/50 border-slate-600 text-white"
                  />
                </div>

                <div className="p-3 bg-amber-900/30 border border-amber-700/50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5" />
                    <p className="text-xs text-amber-300">
                      Após alterar a senha, guarde-a em local seguro. Esta ação será registrada no sistema.
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => setRecoveryStep(1)}
                    className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    Voltar
                  </Button>
                  <Button 
                    onClick={handleRecoverySubmit}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-500"
                    disabled={recoveryLoading || !recoveryForm.newPassword || !recoveryForm.confirmPassword}
                  >
                    {recoveryLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Alterar Senha'
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
